import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import logoFigure from "@/assets/logo-arzani-white.webp";
import parisSeineBoat from "@/assets/paris-seine-boat.jpg";
import heroImage from "@/assets/dest-paris-new.webp";
import destSea from "@/assets/dest-sea.webp";
import residencesHero from "@/assets/residences-hero.webp";
import lifestyleHero from "@/assets/lifestyle-hero.webp";
import villaInterior from "@/assets/villa-interior.webp";
import lifestyleRestaurant from "@/assets/lifestyle-restaurant.webp";
import lifestyleDining from "@/assets/lifestyle-dining-ibiza.webp";
import lifestyleChauffeur from "@/assets/lifestyle-chauffeur.webp";
import parisQuoteImage from "@/assets/paris-quote-image.avif";
import residencesCity from "@/assets/residences-city.webp";
import residencesParisApartment from "@/assets/residences-paris-apartment.webp";
import parisGarden from "@/assets/paris-garden.jpg";
import parisApartmentInterior from "@/assets/paris-apartment-interior.jpg";
import parisChauffeur from "@/assets/paris-chauffeur.webp";
import parisSunset from "@/assets/paris-sunset.jpg";

const ease = [0.22, 1, 0.36, 1] as const;

const services = [
  {
    title: "Residences",
    description:
      "A curated selection of private apartments in Paris' most desirable locations, from Saint-Germain-des-Prés to the 8th arrondissement and Le Marais. Each residence is selected for its design, location, and sense of privacy — offering a refined and comfortable way to experience the city.",
    image: parisApartmentInterior,
  },
  {
    title: "Chauffeur Service",
    description:
      "Private drivers and luxury vehicles arranged throughout your stay, ensuring effortless movement across Paris. From airport arrivals to daily transport between appointments, restaurants, and shopping districts, every journey is handled with precision and ease.",
    image: parisChauffeur,
  },
  {
    title: "Lifestyle",
    description:
      "Paris is experienced through its details. From dining at L'Avenue, Girafe, Costes, and Caviar Kaspia, to moments across Saint-Germain-des-Prés, Le Marais, and Palais Royal, each experience is carefully curated to reflect the pace and character of the city. From shopping along Avenue Montaigne and Rue Saint-Honoré to quiet walks near the Seine and Place Vendôme, every element is aligned with intention. Reservations, access, and planning are handled in advance, allowing your time to unfold naturally.",
    image: parisSunset,
    link: "/paris/lifestyle",
  },
];

const Paris = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Group Paris — Private Residences, Chauffeur & Lifestyle"
        description="Private apartments, chauffeur service and curated lifestyle across Paris' most distinguished arrondissements."
        path="/paris"
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", serviceType: "Luxury concierge", areaServed: "Paris, France", provider: { "@type": "Organization", name: "Arzani Group" } }}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Paris skyline" className="w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/50 via-foreground/20 to-foreground/60" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-background" />
        </div>
        <div className="relative z-10 text-center px-8 flex flex-col items-center">
          <motion.img
            src={logoFigure}
            alt="Arzani Group"
            className="h-20 md:h-28 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.8, ease }}
          />
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1.8, ease }}
          >
            Arzani Group
          </motion.h1>
          <motion.p
            className="font-label text-primary-foreground/60 tracking-[0.4em] mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1.8, ease }}
          >
            P A R I S
          </motion.p>
          <motion.p
            className="font-accent text-primary-foreground/60 text-sm md:text-base italic max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1.8, ease }}
          >
            Elegance perfected,<br />effortlessly arranged.
          </motion.p>
        </div>
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1.5, ease }}
        >
          <span className="font-label text-primary-foreground/30 text-[8px]">Scroll</span>
          <div className="w-px h-8 bg-primary-foreground/20" />
        </motion.div>
      </section>

      {/* Editorial Intro — Asymmetric */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={parisGarden}
                alt="Paris garden courtyard"
                className="w-full h-[280px] md:h-[420px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-4 lg:col-start-9 flex flex-col justify-end"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                The Destination
              </span>
              <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                Paris moves at its own pace.
              </p>
              <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                From quiet mornings along the Seine to evenings shaped by fine dining and
                understated atmosphere, the city offers a balance between tradition and modern
                refinement. Each arrondissement reveals a different character, where every moment
                is shaped by its surroundings.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Centered Statement */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.p
            className="font-accent text-foreground/60 text-base md:text-lg lg:text-xl leading-[1.8] italic"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            With Arzani, every part of your stay is carefully aligned — allowing you
            to experience Paris with ease, discretion, and clarity.
          </motion.p>
        </div>
      </section>

      {/* Services Header */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
              Our Services
            </span>
            <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase mb-4">
              Curated Services in Paris
            </h2>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-xl">
              Seamless access to refined living, thoughtfully arranged around your stay.
            </p>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-xl mt-3">
              From exceptional apartments and private chauffeurs to curated lifestyle experiences,
              every detail is handled with precision and discretion.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="space-y-20 md:space-y-28">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                  i % 2 === 1 ? "direction-rtl" : ""
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.2, delay: 0.05, ease }}
              >
                <div className={`${i % 2 === 0 ? "lg:col-span-7" : "lg:col-span-7 lg:col-start-6"} overflow-hidden`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[260px] md:h-[360px] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className={`${i % 2 === 0 ? "lg:col-span-4 lg:col-start-9" : "lg:col-span-4 lg:col-start-1 lg:row-start-1"}`}>
                  <h3 className="font-display text-lg md:text-xl text-foreground tracking-[0.12em] uppercase mb-4">
                    Arzani {service.title}
                  </h3>
                  <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-sm mb-6">
                    {service.description}
                  </p>
                  {service.link ? (
                    <Link
                      to={service.link}
                      className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                    >
                      Explore
                    </Link>
                  ) : (
                    <a
                      href="https://wa.me/message/2XEKJ5STR2IZJ1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                    >
                      Inquire
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Book */}
      <section className="bg-background py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row">
            {[
              {
                title: "Submit Your Request",
                text: "Tell us your plans, preferences, and dates in Paris.",
              },
              {
                title: "Curated Options",
                text: "We present a tailored selection of apartments, chauffeurs, and experiences.",
              },
              {
                title: "Confirm & Arrange",
                text: "Once confirmed, every detail is secured and prepared in advance.",
              },
              {
                title: "Experience Paris",
                text: "Arrive and enjoy — everything is already in place.",
              },
            ].map((item, i, arr) => (
              <motion.div
                key={item.title}
                className="flex md:flex-col flex-1 group"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1, ease }}
              >
                <div className="flex flex-col md:flex-row items-center mr-5 md:mr-0 md:mb-6">
                  <div className="w-5 h-5 rounded-full border border-foreground/20 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                  </div>
                  {i < arr.length - 1 && (
                    <div className="w-px md:w-full h-full md:h-px bg-foreground/10 md:flex-1 min-h-[40px] md:min-h-0 ml-0 md:ml-0" />
                  )}
                  {i === arr.length - 1 && <div className="md:flex-1" />}
                </div>
                <div className="pb-8 md:pb-0 md:pr-8">
                  <span className="font-label text-[10px] text-foreground/25 tracking-[0.2em] block mb-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-sm text-foreground tracking-[0.15em] uppercase mb-1.5">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-xs leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Images + Text */}
      <section className="py-16 md:py-28 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-4 flex flex-col gap-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <div>
                <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                  Paris is experienced through its details.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] mb-5">
                  A morning in the Marais.<br />
                  An afternoon along the Left Bank.<br />
                  An evening at a table that matters.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                  Through Arzani, each moment is arranged in advance — allowing you to move
                  through the city with complete ease and clarity.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-7 lg:col-start-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.1, ease }}
            >
              <img
                src={parisSeineBoat}
                alt="Seine boat Paris"
                className="w-full h-[280px] md:h-[400px] object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Block */}
      <section className="py-20 md:py-32 bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={parisQuoteImage}
                alt="Paris atmosphere"
                className="w-full h-[180px] md:h-[240px] object-cover"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="lg:col-span-7 lg:col-start-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.5, delay: 0.1, ease }}
            >
              <p className="font-accent text-primary-foreground/60 text-sm md:text-base lg:text-lg leading-[1.8] italic">
                Our role is to understand how each element connects — so your time
                in Paris moves with the effortless rhythm you expect.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-16 md:py-28 bg-background">
        <div className="container-luxury">
          <div></div>

          <motion.div
            className="max-w-2xl mx-auto text-center mt-14 md:mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            <p className="font-body text-foreground/60 text-xs md:text-sm leading-[2] mb-5">
              From exceptional apartments to curated dining and seamless transport —
              every reservation, every transfer, every detail
              is handled before you arrive.
            </p>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[2]">
              No waiting. No uncertainty. Just access.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.span
            className="font-label text-bronze block mb-6 text-[10px] tracking-[0.3em]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Get in Touch
          </motion.span>
          <motion.h2
            className="font-display text-2xl md:text-4xl text-primary-foreground tracking-[0.12em] uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Paris, the Arzani Way
          </motion.h2>
          <motion.p
            className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[2] mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 1.2, ease }}
          >
            From private apartments and chauffeur services to curated dining and lifestyle
            experiences — let us arrange every detail of your stay in Paris.
          </motion.p>
          <motion.a
            href="https://wa.me/message/2XEKJ5STR2IZJ1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-3 border border-primary-foreground/30 text-primary-foreground/80 text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 1, ease }}
          >
            Get in touch
          </motion.a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Paris;
